import React, { useEffect, useMemo, useState } from "react";
// Hooks
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { Link, useNavigate, useParams } from "react-router";
// actions import
import {
  getByIDHistoryPageOperation,
  updateHistoryPageContent,
  clearHistoryPage,
} from "@/services/historiesPagesServices/historiesPagesAction";
// reactstarp imports
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  FormGroup,
  Col,
  Label,
  InputGroup,
  Input,
  Row,
  InputGroupText,
} from "reactstrap";

// nodejs library that concatenates classes
import classnames from "classnames";
// simple header
import SimpleHeader from "@/components/Headers/SimpleHeader";
// component that will let us load the content or required vars
// component to handle complex type variables =// content required only
import HandleContent from "../UpdatePage/HandleContent";
// component to handle complex type variables =// content optional only
import { resolveContentDeclarations } from "../UpdatePage/updatePageHelpers";
import VariableField from "../../variableFields/VariableField";
import { ToastContainer } from "react-toastify";
import NotificationAlertOptions from "@/layouts/Alerts";
import getErrorMessage, { hasDisplayableError } from "@/utils/getErrorMessage";
import { asCaught } from "@/services/caughtError";
import { linkAwareValueChange } from "../contentVariableHelpers";

// UpdateHistoriesPages renders only the four basic content types inline; the
// complex types are delegated to HandleContent (both statuses) below, which
// skip these same four (the two lists stay disjoint).
const BASIC_CONTENT_TYPES = ["String", "Number", "Boolean", "Link"];

// Host-local String block: this screen never had ContentStringField's
// authorname user-select (pinned), so it keeps its own String DOM — the
// variable=="date" native date input and the rows=5 cols=70 textarea.
type HistoriesStringFieldProps = {
  settingVar: { variable?: string; value?: string };
  onChange: (name: string | undefined, value: string) => void;
};

const HistoriesStringField = ({ settingVar, onChange }: HistoriesStringFieldProps) =>
  settingVar.variable === "date" ? (
    <InputGroup className="input-group-merge">
      <input
        type="date"
        name={settingVar.variable}
        id={settingVar.variable}
        value={settingVar.value}
        onChange={(e) => onChange(settingVar.variable, e.target.value)}
      />
    </InputGroup>
  ) : (
    <InputGroup className="input-group-merge">
      <textarea
        rows={5}
        cols={70}
        className="textarea"
        placeholder={`insert value for ${settingVar.variable}`}
        onChange={(e) => onChange(settingVar.variable, e.target.value)}
        name={settingVar.variable}
        value={settingVar.value}
      />
    </InputGroup>
  );

// Static host override maps — module-level per the SCRUM-643 review rule
// (per-render maps would remount the blocks mid-typing and drop input focus).
const REQUIRED_BASIC_OVERRIDES = {
  String: HistoriesStringField,
};
const OPTIONAL_BASIC_OVERRIDES = {
  String: HistoriesStringField,
  // pinned gap: the Optional column never had a Link branch — a
  // ContentOptional Link entry renders NOTHING on this screen
  Link: null,
};

const UpdateHistoriesPages = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const historiesPagesRedux = useAppSelector((state) => state.historiespages.histories_page);
  const [pageToUpdate, setPageToUpdate] = useState<any>({}); // any: copy of histories_page, typed any in historiesPagesSlice.ts (HistoriesPagesState); no client type for a history page yet
  const [isPageUpdated, setIsPageUpdated] = useState(false);
  // SCRUM-957: a failed load must show a real error, not the "you navigated
  // wrong" fallback — caught in the mount effect below. SCRUM-1014:
  // getByIDHistoryPageOperation is now a createAsyncThunk, so we await
  // .unwrap() to surface a rejection to that catch.
  const [loadFailed, setLoadFailed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        await dispatch(getByIDHistoryPageOperation(params.id ?? "")).unwrap();
      } catch {
        setLoadFailed(true);
      }
    };
    setLoadFailed(false);
    load();
  }, [params.id, dispatch]);

  useEffect(() => {
    if (Object.keys(historiesPagesRedux).length > 0 && !isPageUpdated) {
      setPageToUpdate(historiesPagesRedux);
    }
  }, [historiesPagesRedux]);

    useEffect(() => {
      return () => {
        dispatch(clearHistoryPage())
      };
    }, []);


// Change global title
const onChangeGlobalTitle = (e) => {
  setPageToUpdate({
    ...pageToUpdate,
    global_title: e.target.value
  });
};

// Change meta description
const onChangeMetaDescription = (e) => {
  setPageToUpdate({
    ...pageToUpdate,
    meta_description: e.target.value
  });
};

  // SCRUM-1508: which input renders comes from the TEMPLATE's declaration, not
  // from the page's stored copy of it — 52 of 289 compared entries on the live
  // pending pages disagree there, and a `faq` variable still stored as `String`
  // used to get a plain textarea instead of its embed.
  //
  // For rendering only. `pageToUpdate` itself stays as loaded, because the save
  // PUTs it whole and rewriting the stored declaration from an edit screen
  // would be an uncontrolled partial migration — that is SCRUM-1472 slice 4.
  const resolvedContent = useMemo(
    () =>
      resolveContentDeclarations(
        pageToUpdate.page_content,
        pageToUpdate.page_template
      ),
    [pageToUpdate.page_content, pageToUpdate.page_template]
  );

  // What the embeds read. Same document, resolved declarations, and never the
  // object that gets saved.
  //
  // When nothing needed resolving, `resolveContentDeclarations` hands back the
  // very array it was given, and this hands back the very page — so a page
  // without drift reaches the embeds as the identical object it always did.
  // That keeps the SCRUM-853 prop-identity characterisation honest instead of
  // making it assert a wrapper this change introduced.
  const resolvedPage = useMemo(
    () =>
      resolvedContent === pageToUpdate.page_content
        ? pageToUpdate
        : { ...pageToUpdate, page_content: resolvedContent },
    [pageToUpdate, resolvedContent]
  );

  // SCRUM-853: immutable adapters replace the SCRUM-753-class mutation — the
  // redux histories_page doc is no longer written in place by inline edits.
  const changeVariableValue = (name, value) => {
    setPageToUpdate((prev) => ({
      ...prev,
      page_content: prev.page_content.map((contentVar) =>
        contentVar.variable === name ? { ...contentVar, value } : contentVar
      ),
    }));
  };

  // Case variable type is Link  => Special case => prepare object to hold both name and link
  const handleLinkInputRequired = (event, targetVariableName) => {
    setPageToUpdate({
      ...pageToUpdate,
      page_content: pageToUpdate.page_content.map((ContentVar) =>
        ContentVar.variable === targetVariableName
          ? event.target.name === "name"
            ? {
                ...ContentVar,
                value: { ...ContentVar.value, name: event.target.value },
              }
            : {
                ...ContentVar,
                value: { ...ContentVar.value, link: event.target.value },
              }
          : ContentVar
      ),
    });
  };

  // Registry adapter: blocks report (name, value). Link edits are converted
  // back to the edited half so handleLinkInputRequired's per-half merge stays
  // the Link channel (linkAwareValueChange in contentVariableHelpers.ts, shared with UpdatePageView).
  const valueChangeFor = (contentVar) =>
    linkAwareValueChange(contentVar, handleLinkInputRequired, changeVariableValue);

  // Notification
  let notify = (type, msg) => {
    NotificationAlertOptions(type, msg);
  };

  // Handle variable changes from HandleContent (required) component.
  //
  // SCRUM-1508: take the VALUE only, not the whole reported entry. The embeds
  // are handed `resolvedPage`, so the entry they report back carries the
  // template's declaration; writing it whole would persist that on the next
  // save. Behaviour-preserving on its own — the embeds only ever build
  // `{ ...v, value: next }` (the former Required host (.jsx):118, :140) — and it is
  // what keeps the stored copy out of this slice.
  const handleRequiredVariableChange = (updatedVariable) => {
    setPageToUpdate(prevState => ({
      ...prevState,
      page_content: prevState.page_content.map(contentVar =>
        contentVar.variable === updatedVariable.variable
          ? { ...contentVar, value: updatedVariable.value }
          : contentVar
      )
    }));
  };

  // SCRUM-853 D1(a): the Optional embed's sitemap channel reports through
  // this {target:{name,value}} envelope (mirrors UpdatePageContainer's
  // onChangeContentOptional wiring).
  const onChangeContentOptional = (e) => {
    changeVariableValue(e.target.name, e.target.value);
  };

  const updatePageContent = async () => {
    try {
      await dispatch(updateHistoryPageContent(pageToUpdate));
      setIsPageUpdated(true);
      notify("success", `Page with path: ${pageToUpdate.page_configuration.path} Check it in pending pages section`);
      setTimeout(() => {
        navigate("/admin/updated-pages");
      }, 2000);
    } catch (error) {
      // SCRUM-1263: the thunk rejects with the original axios error, so the server's
      // own text sits in error.response.data. error.message is only the generic
      // "Request failed with status code NNN" — read the body first, and fall back to
      // the transport message when there is no response at all.
      const body = asCaught(error)?.response?.data;
      NotificationAlertOptions(
        "error",
        getErrorMessage(hasDisplayableError(body) ? body : { message: asCaught(error)?.message })
      );
      console.error("Failed to update:", error);
    }
  };

  if (Object.keys(pageToUpdate).length > 0) {
    return (
      <>
        <SimpleHeader name="Update History page" parentName="Pages" />
        <Container className="mt--6" fluid>
          <div className="rna-wrapper">
          <ToastContainer />
          </div>
          <Card>
            <CardHeader className="text-center">
              <h3 className="mb-0">
                {" "}
                Update &nbsp;
                <span className="text-primary">
                  {pageToUpdate.global_title}{" "}
                </span>
                content
              </h3>
            </CardHeader>
            <CardBody className="text-center">
                  {/* ************START GOLBAL title + meta description******************** */}

                  <FormGroup className="row">
                    <Label
                      className="form-control-label"
                      htmlFor="example-text-input"
                      md="2"
                    >
                      Global title
                    </Label>
                    <Col md="10">
                      <InputGroup
                        className={classnames("input-group-merge", {
                          focused: pageToUpdate.global_title,
                        })}
                      >

                          <InputGroupText>
                            <i className="ni ni-world-2 text-primary" />
                          </InputGroupText>
                        <Input
                          placeholder="Insert Global title"
                          type="text"
                          value={pageToUpdate.global_title}
                          onChange={e => onChangeGlobalTitle(e)}
                          name="global_title"
                          required
                        />
                      </InputGroup>

                    </Col>
                  </FormGroup>

                  <FormGroup className="row">
                    <Label
                      className="form-control-label"
                      htmlFor="example-text-input"
                      md="2"
                    >
                      META description
                    </Label>
                    <Col md="10">
                      <InputGroup
                        className={classnames("input-group-merge", {
                          focused: pageToUpdate.meta_description,
                        })}
                      >

                          <InputGroupText>
                            <i className="ni ni-collection text-primary" />
                          </InputGroupText>
                        <Input
                          placeholder="Insert META description"
                          rows="3"
                          type="textarea"
                          value={pageToUpdate.meta_description}
                          onChange={e => onChangeMetaDescription(e)}
                          name="meta_description"
                          required
                        />
                      </InputGroup>

                    </Col>
                  </FormGroup>
                  {/* ************END GOLBAL title + meta description******************** */}


              {/* ***************SELECT REQUIRED Content variables*************** */}
              <Row>
                <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                  <Col md="10">

                    {/* ALLOW USER TO SET SELECTED CONTENT VARIABLES VALUES => REQUIRED */}

                    {/* variabletype-only filtering (pinned): NO template
                        filter — off-template basic entries keep rendering.
                        SCRUM-1508: resolvedContent, so basic-vs-complex is
                        decided by the template's type and not the page's. */}
                    {resolvedContent
                      .filter((ctn) => ctn.variabletype === "ContentRequired")
                      .map((contentVar) =>
                        BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                          <VariableField
                            key={contentVar.variable}
                            settingVar={contentVar}
                            options={undefined}
                            onChange={valueChangeFor(contentVar)}
                            overrides={REQUIRED_BASIC_OVERRIDES}
                          />
                        ) : null
                      )}
                    <HandleContent status="required"
                      page_to_update={resolvedPage}
                      onVariableChange={handleRequiredVariableChange}
                    />

                    {/* END Required CONTENT VARIABLES VALUES */}
                  </Col>
                </FormGroup>
                {/* ********************END REQUIRED content variables******************* */}

                {/* *******************SELECT OPTIONAL CONTENT VARIABLES***************** */}
                <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                  <Col md="10">

                    {/* ALLOW USER TO SET SELECTED CONTENT VARIABLES VALUES => OPTIONAL */}
                    {resolvedContent
                      .filter((ctn) => ctn.variabletype === "ContentOptional")
                      .map((contentVar) =>
                        BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                          <VariableField
                            key={contentVar.variable}
                            settingVar={contentVar}
                            options={undefined}
                            onChange={valueChangeFor(contentVar)}
                            overrides={OPTIONAL_BASIC_OVERRIDES}
                          />
                        ) : null
                      )}
                      {/* SCRUM-853 D1(a): both Optional channels are wired so
                          complex Optional edits persist at save (the legacy
                          screen passed none and dropped them) */}
                      <HandleContent status="optional"
                        page_to_update={resolvedPage}
                        onChangeContentOptional={onChangeContentOptional}
                        onVariableChange={handleRequiredVariableChange}
                      />
                    {/* END Optioanl CONTENT VARIABLES VALUES */}
                  </Col>
                </FormGroup>
              </Row>
              {/* ***********************END OPTIONAL content variables********************* */}
            </CardBody>
            <div style={{ marginBottom: "20px" }}>
              <button
                className="btn btn-outline-primary mr-1 float-right"
                type="submit"
                onClick={() => updatePageContent()}
              >
                Update
              </button>
              <Link
                to="/admin/updated-pages"
                className="btn btn-outline-default mr-1 float-left"
              >
                Back
              </Link>
            </div>
          </Card>
        </Container>
      </>
    );
  } else if (loadFailed) {
    // SCRUM-957: distinguish a real load failure from the "wrong navigation"
    // fallback below — the server GET rejected, the user did not mis-navigate.
    return (
      <div>
        Failed to load this page version. Please try again, or go back to
        Updated pages and reselect it.
      </div>
    );
  } else {
    return (
      <div>
        Please Go to Pages section then Updated pages then select a page to
        update !{" "}
      </div>
    );
  }
};

export default UpdateHistoriesPages;
